import { useEffect, useMemo, useState } from 'react';
import { usePageInfo } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { gotoDownload } from '@opm/pages/Download';
import { getDouyinHashtagList } from '@opm/services';
import { DBO_TM } from '@op/utils/format';
import type { SortBy } from 'tea-component/lib/table/addons';
import { IDouyinHashtagItem } from '../tabs/DouyinTab/constants';

interface IUseDouyinHashtagDataParams {
  filter: {
    start_time?: string;
    end_time?: string;
  } | null;
  /** 表格排序（服务端语义：在 hook 内对全量榜单排序后再本地分页） */
  sort?: SortBy<any>;
}

/** 接口为日粒度全量返回、本身不分页；前端按 pageSize=20 做本地分页 */
const PAGE_SIZE = 20;

/**
 * Douyin Hashtag 热搜榜数据 Hook
 *
 * 单日全量加载当日 Top 30 数据（接口不分页），前端按 pageSize=20 切片分页。
 * 请求策略参考 useBilibiliHashtagData: setTimeout(0) 防双次触发。
 */
export function useDouyinHashtagData({
  filter,
  sort,
}: IUseDouyinHashtagDataParams) {
  const [records, setRecords] = useState<IDouyinHashtagItem[]>([]);
  const { pageInfo, changePageInfo, resetPageInfo } = usePageInfo({
    defaultPageInfo: { page: 1, page_size: PAGE_SIZE },
  });

  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    clear: () => {
      setRecords([]);
    },
  });

  const params = useMemo(() => {
    if (!filter?.start_time) return null;
    return {
      start_time: filter.start_time,
      end_time: filter.end_time || filter.start_time,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  function request() {
    if (!params) return;

    wrapperCancelRequest(getDouyinHashtagList, params)
      .then((res: any) => {
        if (!res) return;

        // 扁平化嵌套响应: list[].date + list[].list[] → IDouyinHashtagItem[]
        const flatRecords: IDouyinHashtagItem[] = [];
        if (Array.isArray(res.list)) {
          res.list.forEach((group: any) => {
            const { date } = group;
            if (Array.isArray(group.list)) {
              group.list.forEach((item: any) => {
                flatRecords.push({
                  ...item,
                  rank: item.rank,
                  hashtag: item.hashtag || '',
                  hashtag_zh: item.hashtag_zh || '',
                  hashtag_en: item.hashtag_en || '',
                  heat: item.heat || 0,
                  heat_trend: Array.isArray(item.heat_trend)
                    ? item.heat_trend
                    : [],
                  date,
                });
              });
            }
          });
        }

        // 按日期倒序、rank 升序排序（单日筛选下仅有一日，排序主要兜底多日返回）
        flatRecords.sort((a, b) => {
          if (a.date !== b.date) return a.date < b.date ? 1 : -1;
          return a.rank - b.rank;
        });

        setRecords(flatRecords);
      })
      .catch(() => {
        // 请求取消或失败，不做处理
      });
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      request();
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // 筛选条件变更时重置分页到第一页
  useEffect(() => {
    resetPageInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // 切换排序时重置到第一页，避免停留在与新排序不匹配的页码
  useEffect(() => {
    resetPageInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  const total = records.length;

  // 先对完整榜单按 sort 排序，再做本地分页切片；
  // 否则排序只会在当前页的 pageSize 条内生效（数据是本地分页的）
  const sortedRecords = useMemo(() => {
    if (!sort?.by) return records;
    const by = sort.by as keyof IDouyinHashtagItem;
    const dir = sort.order === 'asc' ? 1 : -1;
    return [...records].sort((a, b) => {
      const av = a[by];
      const bv = b[by];
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * dir;
      }
      return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
    });
  }, [records, sort]);

  // 本地分页切片
  const pageRecords = useMemo(() => {
    const start = (pageInfo.page - 1) * pageInfo.page_size;
    return sortedRecords.slice(start, start + pageInfo.page_size);
  }, [sortedRecords, pageInfo]);

  function download() {
    if (!params || !filter) return;
    gotoDownload({
      key: 'MarketingHashtagDouyinDownload',
      fileName: `Hashtag_Douyin_${DBO_TM.timeTitle(filter as any)}`,
      filter: params,
      data: records,
    });
  }

  return {
    records: pageRecords,
    total,
    loading,
    pageInfo,
    changePageInfo,
    download,
  };
}
